/**
 * Google OAuth entry point — used on Login + Register pages.
 *
 * Click triggers a full-page redirect (NOT an Axios call) to the
 * backend, which builds the Google authorize URL and redirects the
 * browser there. Same-origin → cookies and CSRF tokens follow along
 * for the round-trip.
 *
 * The visual is the standard "Sign in with Google" white-button
 * treatment: white background, 1px border, Google's four-color "G"
 * mark, and a localized label that flips between "Sign in" and
 * "Sign up" via the ``mode`` prop.
 */
import { API_BASE_URL } from "@/lib/constants";

interface GoogleAuthButtonProps {
  /** "signin" → "Sign in with Google", "signup" → "Sign up with Google". */
  mode?: "signin" | "signup";
  /** Optional disabled state — useful while another auth path is mid-submit. */
  disabled?: boolean;
}

export function GoogleAuthButton({
  mode = "signin",
  disabled = false,
}: GoogleAuthButtonProps) {
  const label = mode === "signup" ? "Sign up with Google" : "Sign in with Google";

  // Full-page navigation, not an Axios call. The backend's
  // /auth/google/login responds with a 302 to Google's consent
  // screen, which the browser follows. Using window.location.href
  // (not an axios GET) is required so the redirect chain hits the
  // browser-managed cookie jar — the state cookie set on the way
  // out has to survive the trip back through Google.
  const handleClick = () => {
    if (disabled) return;
    // ``API_BASE_URL`` already includes /api/v1 in dev (relative)
    // and prod (absolute), so we just append the route.
    window.location.href = `${API_BASE_URL}/auth/google/login`;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={
        "w-full inline-flex items-center justify-center gap-3 " +
        "rounded-lg border border-border bg-white px-4 py-2.5 " +
        "text-sm font-medium text-[#1f1f1f] " +
        "hover:bg-gray-50 active:bg-gray-100 " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent " +
        "disabled:opacity-60 disabled:cursor-not-allowed " +
        "transition-colors"
      }
      aria-label={label}
    >
      <GoogleGlyph />
      <span>{label}</span>
    </button>
  );
}


/**
 * Inline-SVG Google "G" — official four-color mark. Inlined (vs. an
 * external image) so there's no flash of unstyled content and no
 * additional network request on the public login page.
 */
function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.891-1.741 2.982-4.305 2.982-7.351z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.964-.895 6.618-2.422l-3.232-2.51c-.896.6-2.041.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.998 9.998 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.405 13.9A6.005 6.005 0 0 1 6.09 12c0-.66.114-1.3.314-1.9V7.51H3.064A9.998 9.998 0 0 0 2 12c0 1.614.387 3.14 1.064 4.49l3.341-2.59z"
      />
      <path
        fill="#EA4335"
        d="M12 5.977c1.468 0 2.787.504 3.823 1.495l2.868-2.868C16.96 2.99 14.696 2 12 2A9.998 9.998 0 0 0 3.064 7.51l3.34 2.59C7.19 7.736 9.395 5.977 12 5.977z"
      />
    </svg>
  );
}


/**
 * The "── or continue with email ──" divider. Used right under the
 * Google button on both login + register surfaces. Kept here so any
 * future style change to the surrounding lines + label stays in
 * lock-step with the button.
 */
export function OrEmailDivider({ label = "or continue with email" }: { label?: string }) {
  return (
    <div className="my-5 flex items-center gap-3" role="separator" aria-label={label}>
      <div className="flex-1 h-px bg-border" />
      <span className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}
